import { useState, useEffect } from 'react';
import { useUserStore } from '@/stores/userStore';
import { Moon, Sun, Volume2, VolumeX, Music, Zap, ZapOff } from 'lucide-react';

export default function SettingsPage() {
  const settings = useUserStore((state) => state.settings);
  const updateSettings = useUserStore((state) => state.updateSettings);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Apply theme to document
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(settings.theme);
  }, [settings.theme]);

  const handleToggle = async (key: keyof typeof settings, value: any) => {
    setSaveStatus('saving');
    await updateSettings({ [key]: value });
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus('idle'), 2000);
  };

  return (
    <div className="min-h-screen p-8 bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 text-primary-400 drop-shadow-[0_0_10px_rgba(34,197,94,0.5)]">
            Settings
          </h1>
          <p className="text-primary-200/80">
            Customize your CodeQuest experience
          </p>
        </div>

        {/* Save Status */}
        {saveStatus !== 'idle' && (
          <div className="mb-6 px-4 py-2 rounded-lg bg-primary-500/10 border border-primary-500/30 text-primary-400 text-sm">
            {saveStatus === 'saving' ? '💾 Saving...' : '✅ Settings saved!'}
          </div>
        )}

        {/* Settings Sections */}
        <div className="space-y-6">
          {/* Appearance */}
          <Section title="⚡ Appearance" description="Visual preferences">
            <SettingRow
              icon={settings.theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
              label="Theme"
              description="Choose your preferred color scheme"
            >
              <ThemeToggle
                value={settings.theme}
                onChange={(theme) => handleToggle('theme', theme)}
              />
            </SettingRow>

            <SettingRow
              icon={settings.animationsEnabled ? <Zap size={20} className="text-warning-400" /> : <ZapOff size={20} className="text-gray-500" />}
              label="Animations"
              description="Enable smooth transitions and effects"
            >
              <Toggle
                enabled={settings.animationsEnabled}
                onChange={(enabled) => handleToggle('animationsEnabled', enabled)}
              />
            </SettingRow>
          </Section>

          {/* Audio */}
          <Section title="🎵 Audio" description="Sound preferences">
            <SettingRow
              icon={settings.soundEnabled ? <Volume2 size={20} className="text-primary-400" /> : <VolumeX size={20} className="text-gray-500" />}
              label="Sound Effects"
              description="Play sounds for actions and events"
            >
              <Toggle
                enabled={settings.soundEnabled}
                onChange={(enabled) => handleToggle('soundEnabled', enabled)}
              />
            </SettingRow>

            <SettingRow
              icon={<Music size={20} className={settings.musicEnabled ? "text-purple-400" : "text-gray-500"} />}
              label="Background Music"
              description="Play ambient music while coding"
            >
              <Toggle
                enabled={settings.musicEnabled}
                onChange={(enabled) => handleToggle('musicEnabled', enabled)}
              />
            </SettingRow>
          </Section>

          {/* Info */}
          <div className="mt-8 p-4 rounded-lg bg-primary-500/5 border border-primary-500/20 text-sm text-primary-200/60">
            💡 <span className="font-semibold">Tip:</span> Settings are automatically saved to the cloud when you're signed in
          </div>
        </div>
      </div>
    </div>
  );
}

// Section Component
function Section({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div className="bg-gray-900/50 backdrop-blur-sm border border-primary-500/20 rounded-lg p-6 shadow-lg hover:border-primary-500/40 transition-all">
      <h2 className="text-xl font-semibold text-primary-400 mb-1">{title}</h2>
      <p className="text-sm text-primary-200/60 mb-4">{description}</p>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
}

// Setting Row Component
function SettingRow({ icon, label, description, children }: {
  icon: React.ReactNode;
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-primary-500/10 last:border-0">
      <div className="flex items-start space-x-3 flex-1">
        <div className="mt-1 text-primary-400">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-primary-200 font-medium mb-1">{label}</h3>
          <p className="text-sm text-primary-200/50">{description}</p>
        </div>
      </div>
      <div className="ml-4">
        {children}
      </div>
    </div>
  );
}

// Theme Toggle Component
function ThemeToggle({ value, onChange }: { value: 'light' | 'dark'; onChange: (theme: 'light' | 'dark') => void }) {
  return (
    <div className="flex items-center space-x-2 bg-black/30 rounded-lg p-1">
      <button
        onClick={() => onChange('dark')}
        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
          value === 'dark'
            ? 'bg-primary-500 text-black shadow-lg shadow-primary-500/30'
            : 'text-primary-200/60 hover:text-primary-200'
        }`}
      >
        <Moon size={16} className="inline mr-1" />
        Dark
      </button>
      <button
        onClick={() => onChange('light')}
        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
          value === 'light'
            ? 'bg-primary-500 text-black shadow-lg shadow-primary-500/30'
            : 'text-primary-200/60 hover:text-primary-200'
        }`}
      >
        <Sun size={16} className="inline mr-1" />
        Light
      </button>
    </div>
  );
}

// Toggle Switch Component
function Toggle({ enabled, onChange }: { enabled: boolean; onChange: (enabled: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
        enabled ? 'bg-primary-500' : 'bg-gray-700'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}
